import { useState, useEffect, useCallback } from 'react';
import {
  SlidersHorizontal, ClipboardList, ArrowUpCircle, ArrowDownCircle,
  RefreshCw, CheckCircle2, Search, ChevronDown, AlertTriangle, Package,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { cantidad } from '../utils/format';

// ── Config ────────────────────────────────────────────────────────────────
const TIPOS_AJUSTE = [
  { value: 'ajuste_entrada', label: 'Entrada',   desc: 'Corrección / devolución',    Icon: ArrowUpCircle,   color: 'text-teal-500'  },
  { value: 'ajuste_salida',  label: 'Salida',    desc: 'Corrección de excedente',    Icon: ArrowDownCircle, color: 'text-amber-500' },
  { value: 'merma',          label: 'Merma',     desc: 'Pérdida / vencimiento',      Icon: ArrowDownCircle, color: 'text-red-500'   },
];

// ── Tab switcher ──────────────────────────────────────────────────────────
function TabBtn({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all
        ${active
          ? 'bg-primary-500 text-white shadow-sm'
          : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
    >
      <Icon size={15} />
      {label}
    </button>
  );
}

// ── Ajuste individual ─────────────────────────────────────────────────────
function AjusteIndividual({ productos }) {
  const [form, setForm]     = useState({ producto_id: '', tipo: 'ajuste_entrada', cantidad: '', notas: '' });
  const [saving, setSaving] = useState(false);

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));
  const esSalida = ['ajuste_salida', 'merma'].includes(form.tipo);
  const prod     = productos.find(p => p.id === parseInt(form.producto_id));

  const stockPreview = () => {
    if (!prod || !form.cantidad || parseFloat(form.cantidad) <= 0) return null;
    const delta = esSalida ? -parseFloat(form.cantidad) : parseFloat(form.cantidad);
    return prod.stock_actual + delta;
  };

  const handleSave = async () => {
    if (!form.producto_id)                       { toast.error('Seleccioná un producto'); return; }
    if (!form.cantidad || parseFloat(form.cantidad) <= 0) { toast.error('La cantidad debe ser mayor a 0'); return; }
    if (esSalida && !form.notas.trim())           { toast.error('El motivo es obligatorio para salidas y mermas'); return; }

    setSaving(true);
    try {
      const res = await api.post('/stock/ajuste.php', {
        producto_id: parseInt(form.producto_id),
        tipo:        form.tipo,
        cantidad:    parseFloat(form.cantidad),
        notas:       form.notas.trim(),
      });
      toast.success(`Ajuste registrado — Stock nuevo: ${cantidad(res.data.stock_despues, prod?.unidad)}`);
      setForm({ producto_id: '', tipo: 'ajuste_entrada', cantidad: '', notas: '' });
      // Actualizar stock local del producto
      prod.stock_actual = res.data.stock_despues;
    } catch (err) {
      toast.error(err?.message || 'Error al registrar ajuste');
    } finally { setSaving(false); }
  };

  const preview = stockPreview();

  return (
    <div className="card max-w-lg space-y-5">
      <div>
        <label>Producto *</label>
        <select className="input" value={form.producto_id} onChange={set('producto_id')}>
          <option value="">Seleccionar producto...</option>
          {productos.map(p => (
            <option key={p.id} value={p.id}>
              {p.nombre}{p.sku ? ` (${p.sku})` : ''} — stock: {cantidad(p.stock_actual, p.unidad)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label>Tipo de ajuste *</label>
        <div className="grid grid-cols-3 gap-2">
          {TIPOS_AJUSTE.map(t => {
            const active = form.tipo === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setForm(p => ({ ...p, tipo: t.value }))}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all
                  ${active
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'}`}
              >
                <t.Icon size={18} className={active ? 'text-primary-500' : t.color} />
                <span className={`text-xs font-semibold ${active ? 'text-primary-600 dark:text-primary-400' : 'text-slate-700 dark:text-slate-300'}`}>
                  {t.label}
                </span>
                <span className="text-[10px] text-slate-400 leading-tight">{t.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label>Cantidad *</label>
        <input
          className="input"
          type="number"
          min="0.001"
          step="0.001"
          placeholder="0"
          value={form.cantidad}
          onChange={set('cantidad')}
        />
      </div>

      <div>
        <label>{esSalida ? 'Motivo *' : 'Notas (opcional)'}</label>
        <textarea
          className="input resize-none"
          rows={2}
          placeholder={esSalida ? 'Describí el motivo de la salida...' : 'Observaciones...'}
          value={form.notas}
          onChange={set('notas')}
        />
      </div>

      {prod && preview !== null && (
        <div className={`p-4 rounded-xl text-sm flex items-center gap-3
          ${esSalida ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400' : 'bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400'}`}>
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0
            ${esSalida ? 'bg-red-100 dark:bg-red-500/20' : 'bg-teal-100 dark:bg-teal-500/20'}`}>
            {esSalida ? <ArrowDownCircle size={16} /> : <ArrowUpCircle size={16} />}
          </div>
          <div>
            <p className="font-semibold">{prod.nombre}</p>
            <p className="text-xs mt-0.5">
              Stock actual: <strong>{cantidad(prod.stock_actual, prod.unidad)}</strong>
              {' → '}
              <strong>{cantidad(preview, prod.unidad)}</strong>
              {' '}
              <span className="font-normal">
                ({esSalida ? '' : '+'}{cantidad(esSalida ? -parseFloat(form.cantidad) : parseFloat(form.cantidad), prod.unidad)})
              </span>
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2 border-t border-slate-100 dark:border-slate-700">
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          <SlidersHorizontal size={15} />
          {saving ? 'Registrando...' : 'Registrar ajuste'}
        </button>
      </div>
    </div>
  );
}

// ── Toma de inventario ────────────────────────────────────────────────────
function TomaInventario({ productos, categorias }) {
  const [counts,     setCounts]     = useState({});   // { producto_id: string }
  const [notas,      setNotas]      = useState('');
  const [search,     setSearch]     = useState('');
  const [catFilter,  setCatFilter]  = useState('');
  const [saving,     setSaving]     = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const setCount = (id, val) => setCounts(p => ({ ...p, [id]: val }));

  const filtrados = productos.filter(p => {
    const matchSearch = !search || p.nombre.toLowerCase().includes(search.toLowerCase()) || (p.sku || '').toLowerCase().includes(search.toLowerCase());
    const matchCat    = !catFilter || p.categoria === catFilter;
    return matchSearch && matchCat;
  });

  // Solo los que tienen diferencia real
  const conDiferencia = filtrados.filter(p => {
    const fisica = parseFloat(counts[p.id] ?? '');
    return !isNaN(fisica) && Math.abs(fisica - p.stock_actual) >= 0.001;
  });

  const handleConfirm = async () => {
    if (conDiferencia.length === 0) { toast.error('No hay diferencias para ajustar'); return; }
    setSaving(true);
    try {
      const items = conDiferencia.map(p => ({
        producto_id:    p.id,
        cantidad_fisica: parseFloat(counts[p.id]),
        notas:           notas.trim() || 'Toma de inventario',
      }));
      const res = await api.post('/stock/toma-inventario.php', { items });
      toast.success(res.data.message || 'Toma de inventario completada');
      setCounts({});
      setConfirmOpen(false);
    } catch (err) {
      toast.error(err?.message || 'Error al procesar');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Controles */}
      <div className="card !p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-8 py-2 text-sm"
            placeholder="Buscar producto o SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="input w-44 py-2 text-sm" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
          <option value="">Todas las categorías</option>
          {categorias.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {Object.keys(counts).length > 0 && (
          <button className="btn-ghost text-sm text-slate-400" onClick={() => setCounts({})}>
            Limpiar conteos
          </button>
        )}
      </div>

      {/* Banner de diferencias pendientes */}
      {conDiferencia.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-2xl p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertTriangle size={18} className="text-amber-500 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                {conDiferencia.length} producto{conDiferencia.length !== 1 ? 's' : ''} con diferencia
              </p>
              <p className="text-xs text-amber-500 mt-0.5">Revisá las diferencias antes de confirmar</p>
            </div>
          </div>
          <button
            className="btn-primary bg-amber-500 hover:bg-amber-600 text-white shrink-0"
            onClick={() => setConfirmOpen(true)}
          >
            <CheckCircle2 size={15} />
            Confirmar toma
          </button>
        </div>
      )}

      {/* Tabla */}
      <div className="card !p-0 overflow-hidden">
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Categoría</th>
                <th className="text-right">Stock sistema</th>
                <th className="text-right w-40">Conteo físico</th>
                <th className="text-right">Diferencia</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-400">
                    Sin productos
                  </td>
                </tr>
              ) : (
                filtrados.map(p => {
                  const rawVal  = counts[p.id] ?? '';
                  const fisica  = parseFloat(rawVal);
                  const diff    = !isNaN(fisica) ? fisica - p.stock_actual : null;
                  const hayDiff = diff !== null && Math.abs(diff) >= 0.001;

                  return (
                    <tr key={p.id} className={hayDiff ? 'bg-amber-50/50 dark:bg-amber-500/5' : ''}>
                      <td>
                        <div>
                          <p className="font-medium text-slate-800 dark:text-slate-200 text-sm">{p.nombre}</p>
                          {p.sku && <p className="text-[11px] text-slate-400 font-mono">{p.sku}</p>}
                        </div>
                      </td>
                      <td className="text-slate-500 dark:text-slate-400 text-xs">{p.categoria}</td>
                      <td className="text-right tabular-nums font-semibold text-slate-700 dark:text-slate-300">
                        {cantidad(p.stock_actual, p.unidad)}
                      </td>
                      <td className="text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          className={`input py-1.5 text-sm text-right w-32 tabular-nums
                            ${hayDiff ? 'ring-2 ring-amber-300 dark:ring-amber-500/50 bg-amber-50 dark:bg-amber-500/10' : ''}`}
                          placeholder={cantidad(p.stock_actual)}
                          value={rawVal}
                          onChange={(e) => setCount(p.id, e.target.value)}
                        />
                      </td>
                      <td className="text-right tabular-nums font-semibold">
                        {!hayDiff ? (
                          <span className="text-slate-300 dark:text-slate-600">—</span>
                        ) : diff > 0 ? (
                          <span className="text-teal-600 dark:text-teal-400">+{cantidad(diff, p.unidad)}</span>
                        ) : (
                          <span className="text-red-500 dark:text-red-400">{cantidad(diff, p.unidad)}</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700">
          <p className="text-xs text-slate-400">{filtrados.length} producto/s — Ingresá el conteo físico solo donde haya diferencia</p>
        </div>
      </div>

      {/* Modal de confirmación */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800">
              <h2 className="text-base font-bold text-slate-900 dark:text-white">Confirmar toma de inventario</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Se registrarán {conDiferencia.length} ajuste{conDiferencia.length !== 1 ? 's' : ''}. Esta acción no se puede deshacer.
              </p>
            </div>

            <div className="p-6 space-y-4 max-h-72 overflow-y-auto">
              {conDiferencia.map(p => {
                const fisica = parseFloat(counts[p.id]);
                const diff   = fisica - p.stock_actual;
                return (
                  <div key={p.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium text-slate-700 dark:text-slate-300 truncate">{p.nombre}</span>
                    <div className="flex items-center gap-2 shrink-0 tabular-nums">
                      <span className="text-slate-400">{cantidad(p.stock_actual, p.unidad)}</span>
                      <span className="text-slate-300 dark:text-slate-600">→</span>
                      <span className="font-semibold text-slate-800 dark:text-slate-200">{cantidad(fisica, p.unidad)}</span>
                      <span className={`text-xs font-bold ${diff > 0 ? 'text-teal-500' : 'text-red-500'}`}>
                        ({diff > 0 ? '+' : ''}{cantidad(diff, p.unidad)})
                      </span>
                    </div>
                  </div>
                );
              })}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  Notas generales
                </label>
                <input
                  className="input mt-1 text-sm"
                  placeholder="Toma de inventario mensual..."
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
              <button className="btn-secondary" onClick={() => setConfirmOpen(false)} disabled={saving}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={handleConfirm} disabled={saving}>
                <CheckCircle2 size={15} />
                {saving ? 'Procesando...' : 'Confirmar y registrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page principal ────────────────────────────────────────────────────────
export default function Ajustes() {
  const { can }  = useAuth();
  const canAjust = can('stock.ajustar');

  const [tab,       setTab]       = useState('individual');
  const [productos, setProductos] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const fetchProductos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/productos/index.php');
      setProductos(res.data.filter(p => p.activo !== false && p.activo !== 0));
    } catch { toast.error('Error al cargar productos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProductos(); }, [fetchProductos]);

  const categorias = [...new Set(productos.map(p => p.categoria).filter(Boolean))].sort();

  if (!canAjust) {
    return (
      <div className="card flex flex-col items-center justify-center py-20 text-slate-300 dark:text-slate-600">
        <Package size={48} />
        <p className="text-sm mt-3 text-slate-500 dark:text-slate-400">Sin permiso para registrar ajustes</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Ajustes de stock</h1>
        <button
          className="btn-ghost text-sm text-slate-400 flex items-center gap-1.5"
          onClick={fetchProductos}
          title="Actualizar stock"
        >
          <RefreshCw size={14} />
          Actualizar
        </button>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-fit">
        <TabBtn
          active={tab === 'individual'}
          onClick={() => setTab('individual')}
          icon={SlidersHorizontal}
          label="Ajuste individual"
        />
        <TabBtn
          active={tab === 'toma'}
          onClick={() => setTab('toma')}
          icon={ClipboardList}
          label="Toma de inventario"
        />
      </div>

      {loading ? (
        <div className="card flex justify-center py-16">
          <div className="w-7 h-7 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : tab === 'individual' ? (
        <AjusteIndividual productos={productos} />
      ) : (
        <TomaInventario productos={productos} categorias={categorias} />
      )}
    </div>
  );
}
